/** Screening cash flows only: inputs must come from a separate building assessment. */
export type SavingsInputs = {
  cooling: number
  fraction: number
  reduction: number
  rate: number
  quote: number
  heating: number
  maintenance: number
  years: number
  discount: number
}

/** Invented arithmetic example, not prices, tariffs, building averages or product claims. */
export const hypotheticalExample: SavingsInputs = {
  cooling: 100000, fraction: 30, reduction: 20, rate: 0.18,
  quote: 8000, heating: 180, maintenance: 100, years: 10, discount: 5,
}

export function calculateSavings(p: SavingsInputs) {
  if (Object.keys(hypotheticalExample).some(key => {
    const value = p[key as keyof SavingsInputs]
    return typeof value !== 'number' || !Number.isFinite(value) || value < 0
  })) throw new Error('Complete every field with a finite, non-negative number.')
  if (Object.values(p).some(value => value > 1e12)) throw new Error('Inputs must be no greater than one trillion for this screening calculation.')
  if (p.fraction > 100 || p.reduction > 100 || p.discount > 100) throw new Error('Percentages must be between 0 and 100.')
  if (!Number.isInteger(p.years) || p.years < 1 || p.years > 50) throw new Error('Study period must be a whole number from 1 to 50 years.')
  const avoidedKwh = p.cooling * p.fraction / 100 * p.reduction / 100
  const gross = avoidedKwh * p.rate
  const net = gross - p.heating - p.maintenance
  if (![avoidedKwh, gross, net].every(Number.isFinite)) throw new Error('These values are too large to calculate. Reduce the inputs.')
  let cumulative = -p.quote
  let discountedCumulative = -p.quote
  const rows = [{ year: 0, cashflow: -p.quote, discounted: -p.quote, cumulative, discountedCumulative }]
  for (let year = 1; year <= p.years; year++) {
    const discounted = net / (1 + p.discount / 100) ** year
    cumulative += net
    discountedCumulative += discounted
    if (![cumulative, discountedCumulative].every(Number.isFinite)) throw new Error('These values are too large to calculate. Reduce the inputs.')
    rows.push({ year, cashflow: net, discounted, cumulative, discountedCumulative })
  }
  return {
    avoidedKwh, gross, net, rows, npv: discountedCumulative, total: cumulative,
    simplePayback: p.quote === 0 ? 0 : net > 0 ? p.quote / net : null,
    discountedPayback: p.quote === 0 ? 0 : rows.slice(1).find(row => row.discountedCumulative >= 0)?.year ?? null,
  }
}

export function savingsCsv(inputs: SavingsInputs): string {
  const result = calculateSavings(inputs)
  return [
    'Hypothetical illustration - all currency CAD - constant real annual cash flows',
    'Input,Value', ...Object.entries(inputs).map(([key, value]) => `${key},${value}`), '',
    'Year,Net flow CAD,Discounted flow CAD,Cumulative CAD,Discounted cumulative CAD',
    ...result.rows.map(row => [row.year, row.cashflow, row.discounted, row.cumulative, row.discountedCumulative].join(',')),
  ].join('\n')
}
